import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getSaaSMetricsSummary, getMonthlyTargets, setMonthlyTarget } from '../../../utils/dashboardService';

const SaaSMetricsCards = () => {
  const [metrics, setMetrics] = useState(null);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [metricsData, targetsData] = await Promise.all([
        getSaaSMetricsSummary(),
        getMonthlyTargets()
      ]);
      
      setMetrics(metricsData);
      setTargets(targetsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching SaaS metrics:', err);
      setError('Failed to load SaaS metrics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatPercentage = (num) => {
    return `${num.toFixed(1)}%`;
  };

  const getTargetForMetric = (metricName) => {
    // targets is an object with revenue, subscribers, churn properties
    // Convert to the expected format for the component
    if (!targets || typeof targets !== 'object') return null;
    
    switch (metricName) {
      case 'mrr':
        return targets.revenue ? {
          metric_name: 'mrr',
          target_value: targets.revenue.target,
          current_value: targets.revenue.current,
          progress_percentage: targets.revenue.target > 0 ? (targets.revenue.current / targets.revenue.target) * 100 : 0,
          is_on_track: targets.revenue.target > 0 ? targets.revenue.current >= targets.revenue.target * 0.8 : true
        } : null;
      case 'active_subscriptions':
        return targets.subscribers ? {
          metric_name: 'active_subscriptions',
          target_value: targets.subscribers.target,
          current_value: targets.subscribers.current,
          progress_percentage: targets.subscribers.target > 0 ? (targets.subscribers.current / targets.subscribers.target) * 100 : 0,
          is_on_track: targets.subscribers.target > 0 ? targets.subscribers.current >= targets.subscribers.target * 0.8 : true
        } : null;
      case 'churn_rate':
        return targets.churn ? {
          metric_name: 'churn_rate',
          target_value: targets.churn.target,
          current_value: targets.churn.current,
          progress_percentage: targets.churn.target > 0 ? Math.max(0, 100 - ((targets.churn.current / targets.churn.target) * 100)) : 100,
          is_on_track: targets.churn.current <= targets.churn.target
        } : null;
      default:
        return null;
    }
  };

  const getMetricColor = (value, target, isPercentage = false) => {
    if (!target) return 'text-foreground';
    
    const progress = target.progress_percentage;
    if (progress >= 100) return 'text-accent';
    if (progress >= 80) return 'text-primary';
    if (progress >= 60) return 'text-secondary';
    return 'text-destructive';
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'bg-accent';
    if (progress >= 80) return 'bg-primary';
    if (progress >= 60) return 'bg-secondary';
    return 'bg-destructive';
  };

  const handleSetTarget = async (metricName, targetValue) => {
    try {
      const result = await setMonthlyTarget(metricName, targetValue);
      if (result.success) {
        await fetchData(); // Refresh data
        setShowTargetModal(false);
        setSelectedMetric(null);
      } else {
        console.error('Failed to set target:', result.error);
      }
    } catch (error) {
      console.error('Error setting target:', error);
    }
  };

  if (loading) {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">SaaS Metrics</h2>
            <p className="text-sm text-muted-foreground">Key subscription business indicators</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="bg-card border border-border rounded-lg p-6 animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-muted rounded-lg"></div>
                <div className="w-16 h-4 bg-muted rounded"></div>
              </div>
              <div className="w-20 h-8 bg-muted rounded mb-2"></div>
              <div className="w-24 h-4 bg-muted rounded mb-4"></div>
              <div className="w-full h-2 bg-muted rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-8">
        <div className="flex items-center space-x-2">
          <Icon name="AlertTriangle" size={20} className="text-destructive" />
          <span className="text-destructive font-medium">{error}</span>
        </div>
      </div>
    );
  }

  // Calculate ARPU from MRR and active subscriptions
  const arpu = metrics?.active_subscriptions > 0 ? 
    (metrics.mrr / metrics.active_subscriptions) : 0;

  const saasCards = [
    {
      title: 'Monthly Recurring Revenue',
      value: formatCurrency(metrics?.mrr || 0),
      subtitle: 'MRR',
      icon: 'DollarSign',
      color: 'primary',
      metricName: 'mrr',
      description: 'Total monthly subscription revenue',
      trend: 'positive'
    },
    {
      title: 'Annual Run Rate',
      value: formatCurrency(metrics?.arr || 0),
      subtitle: 'ARR (MRR × 12)',
      icon: 'TrendingUp',
      color: 'accent',
      metricName: 'arr',
      description: 'Annualized revenue projection',
      trend: 'positive'
    },
    {
      title: 'Active Subscriptions',
      value: formatNumber(metrics?.active_subscriptions || 0),
      subtitle: 'Paying customers',
      icon: 'Users',
      color: 'secondary',
      metricName: 'active_subscriptions',
      description: 'Currently paying subscribers',
      trend: 'positive'
    },
    {
      title: 'Average Revenue Per User',
      value: formatCurrency(arpu),
      subtitle: 'ARPU',
      icon: 'Target',
      color: 'accent',
      metricName: 'arpu',
      description: 'Monthly revenue per subscriber',
      trend: 'positive'
    },
    {
      title: 'Churn Rate',
      value: formatPercentage(metrics?.churn_rate || 0),
      subtitle: '30-day churn',
      icon: 'UserMinus',
      color: metrics?.churn_rate > 5 ? 'destructive' : 'accent',
      metricName: 'churn_rate',
      description: 'Monthly customer churn rate',
      trend: metrics?.churn_rate > 5 ? 'negative' : 'positive'
    },
    {
      title: 'Trial Conversion',
      value: formatPercentage(metrics?.trial_conversion_rate || 0),
      subtitle: 'Trial → Paid',
      icon: 'UserCheck',
      color: 'primary',
      metricName: 'trial_conversion_rate',
      description: 'Trial to paid conversion rate',
      trend: 'positive'
    },
    {
      title: 'Daily Active Users',
      value: formatNumber(metrics?.dau || 0),
      subtitle: 'DAU',
      icon: 'Activity',
      color: 'secondary',
      metricName: 'dau',
      description: 'Users active today',
      trend: 'positive'
    },
    {
      title: 'Monthly Active Users',
      value: formatNumber(metrics?.mau || 0),
      subtitle: 'MAU',
      icon: 'Users',
      color: 'primary',
      metricName: 'mau',
      description: 'Users active this month',
      trend: 'positive'
    },
    {
      title: 'User Stickiness',
      value: formatPercentage(metrics?.stickiness || 0),
      subtitle: 'DAU/MAU Ratio',
      icon: 'Zap',
      color: metrics?.stickiness > 20 ? 'accent' : 'secondary',
      metricName: 'stickiness',
      description: 'Daily engagement ratio',
      trend: metrics?.stickiness > 20 ? 'positive' : 'neutral'
    }
  ];

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">SaaS Metrics</h2>
          <p className="text-sm text-muted-foreground">Key subscription business indicators</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          iconName="Target"
          onClick={() => setShowTargetModal(true)}
        >
          Set Targets
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {saasCards.map((card, index) => {
          const target = getTargetForMetric(card.metricName);
          const metricColor = getMetricColor(card.value, target);
          
          return (
            <div key={index} className="bg-card border border-border rounded-lg p-6 hover:shadow-gaming transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  card.color === 'primary' ? 'bg-primary/10' :
                  card.color === 'secondary' ? 'bg-secondary/10' :
                  card.color === 'accent' ? 'bg-accent/10' :
                  card.color === 'destructive' ? 'bg-destructive/10' : 'bg-muted/10'
                }`}>
                  <Icon 
                    name={card.icon} 
                    size={24} 
                    className={
                      card.color === 'primary' ? 'text-primary' :
                      card.color === 'secondary' ? 'text-secondary' :
                      card.color === 'accent' ? 'text-accent' :
                      card.color === 'destructive' ? 'text-destructive' : 'text-muted-foreground'
                    }
                  />
                </div>
                {card.trend !== 'neutral' && (
                  <div className={`flex items-center space-x-1 ${
                    card.trend === 'positive' ? 'text-accent' : 'text-destructive'
                  }`}>
                    <Icon 
                      name={card.trend === 'positive' ? 'TrendingUp' : 'TrendingDown'} 
                      size={16} 
                    />
                  </div>
                )}
              </div>
              
              <div>
                <h3 className={`text-2xl font-bold mb-1 ${metricColor}`}>{card.value}</h3>
                <p className="text-sm text-muted-foreground mb-1">{card.title}</p>
                <p className="text-xs font-medium text-foreground mb-3">{card.subtitle}</p>
                
                {target && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Target: {
                        card.metricName.includes('rate') || card.metricName === 'stickiness' 
                          ? formatPercentage(parseFloat(target.target_value))
                          : card.metricName === 'mrr' || card.metricName === 'arr'
                          ? formatCurrency(parseFloat(target.target_value))
                          : formatNumber(parseFloat(target.target_value))
                      }</span>
                      <span className={`font-medium ${
                        target.is_on_track ? 'text-accent' : 'text-destructive'
                      }`}>
                        {target.progress_percentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${getProgressColor(target.progress_percentage)}`}
                        style={{ width: `${Math.min(target.progress_percentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground/80">{card.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Target Setting Modal */}
      {showTargetModal && (
        <TargetModal 
          metrics={saasCards}
          onClose={() => setShowTargetModal(false)}
          onSetTarget={handleSetTarget}
        />
      )}
    </div>
  );
};

// Target Setting Modal Component
const TargetModal = ({ metrics, onClose, onSetTarget }) => {
  const [selectedMetric, setSelectedMetric] = useState('');
  const [targetValue, setTargetValue] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedMetric && targetValue) {
      onSetTarget(selectedMetric, parseFloat(targetValue));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Set Monthly Target</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Metric
            </label>
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="w-full p-2 border border-border rounded-lg bg-background text-foreground"
              required
            >
              <option value="">Select a metric</option>
              {metrics.map(metric => (
                <option key={metric.metricName} value={metric.metricName}>
                  {metric.title}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Target Value
            </label>
            <input
              type="number"
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              className="w-full p-2 border border-border rounded-lg bg-background text-foreground"
              placeholder="Enter target value"
              step="0.01"
              required
            />
          </div>
          
          <div className="flex space-x-3">
            <Button type="submit" className="flex-1">
              Set Target
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaaSMetricsCards;
